<template>
  <b-form-group class="accordion" role="tablist">
    <b-card no-body :class="borderClass">
      <b-card-header header-tag="header" role="tab">
        <div style="cursor: pointer" @click="visible = !visible">CREATININE</div>
      </b-card-header>
      <b-collapse v-model="visible" role="tabpanel">
        <b-card-body>
          <VForm ref="observer" v-slot="{ meta: formMeta }" as="div">
            <b-form @submit.prevent="saveForm">
              <InputDateValidation
                :key="key"
                :field-name="fieldName"
                :field-label="form.method"
                :field-placeholder="fieldLabel"
                :field-date="form.datetime"
                :field-form="form.crcl"
                :field-unit="form.crUnit"
                :unit-list="crUnits"
                :rules-list="crRules"
                @save-input-data="loadInputData"
              ></InputDateValidation>
              <hr />
              <span class="flex align-content-center">
                <b-button
                  v-b-tooltip.hover="
                    'Add serum creatinine — used to calculate renal clearance via Cockcroft-Gault'
                  "
                  class="rounded-circle shadow bg-secondary"
                  :disabled="!formMeta.valid || !isFormValid"
                  @click="saveForm"
                  ><PlusIcon></PlusIcon
                ></b-button>
              </span>
            </b-form>
          </VForm>
        </b-card-body>
      </b-collapse>
    </b-card>
  </b-form-group>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { addDoseFeedback } from '@/components/services/common';
import { isValidDateValue } from '@/models/utils/datetime';
import { DoseTypes, units, ClearanceMethods, getByValue } from '@/models/utils/constants.js';
import { Dose } from '@/models/Dose';
import '@/models/utils/validations';
import InputDateValidation from '@/components/common/InputDateValidation.vue';
import PlusIcon from 'vue-material-design-icons/Plus.vue';
import { useDosingStore } from '@/stores/dosing';
import { useToast } from 'vue-toastification';

defineOptions({ name: 'CreatinineInput' });

const props = defineProps<{ model: any }>();
const emit = defineEmits<{ (e: 'switchTab', evt: any, tab: number): void }>();

const store = useDosingStore();
const visible = ref(false);
const toast = useToast();
const observer = ref<any>(null);

const label = getByValue(ClearanceMethods, props.model.getClearanceMethod())[0];
const fieldName = 'creatinine';
const fieldLabel = label;
const key = ref(0);

function getUnitList() {
  return props.model.clearance
    ? [props.model.clearance[0].methodUnit]
    : props.model.getClearanceMethod() === ClearanceMethods.SeCR
      ? units.creatinineConcentration
      : units.crcl;
}

const crUnits = getUnitList();

function initForm() {
  const unit = getUnitList()[0];
  return {
    method: props.model.getClearanceMethod(),
    crcl: null as number | null,
    crUnit: unit,
    datetime: null as Date | null,
  };
}

const form = ref(initForm());
const crRules = computed(() => ({ secrBetween: { unit: form.value.crUnit } }));

const isFormValid = computed(
  () => isValidDateValue(form.value.datetime) && form.value.crcl !== null,
);

const borderClass = computed(() => {
  const currentDoses = store.getDoses;
  if (!currentDoses?.some((d: any) => d.type === DoseTypes.CLCR || d.type === DoseTypes.SECR)) {
    return 'mb-1 border-danger';
  }
  return 'mb-1';
});

onMounted(() => {
  form.value = initForm();
});

function loadInputData(event: any) {
  form.value = { ...form.value, datetime: event.datetime, crcl: event.value, crUnit: event.unit };
}

function saveForm($event: any) {
  if (isFormValid.value) {
    observer.value.validate().then(({ valid }: { valid: boolean }) => {
      if (valid) {
        const d = new Dose({
          type: form.value.method === ClearanceMethods.CLCR ? DoseTypes.CLCR : DoseTypes.SECR,
          datetime: new Date(form.value.datetime!),
          amount: form.value.crcl,
          amountUnit: form.value.crUnit,
          duration: null,
          durationUnit: null,
          _rowVariant: 'success',
        });
        const feedback = addDoseFeedback(store.addDose(d), 'Creatinine added');
        emit('switchTab', $event, 1);
        if (feedback.type !== 'error') observer.value.resetForm();
        toast[feedback.type](feedback.text);
      }
    });
  }
}
</script>

<style scoped></style>
